import { Link, useNavigate } from "@tanstack/react-router";
import {
  Plus,
  FileText,
  Users,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInvoices } from "@/features/invoices/hooks/use-invoices";
import { useCustomers } from "@/features/customers/hooks/use-customers";
import { useCurrencyFormat } from "@/hooks/use-currency-format";
import type {
  Invoice,
  InvoiceStatus,
} from "@/features/invoices/types/invoice.types";
import type { CustomerWithEmails } from "@/features/customers/types/customer.types";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useDateFormat } from "@/hooks/use-date-format";
import { useMemo, useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useSubscription } from "@/hooks/use-subscription";

export default function DashboardPage() {
  const { t } = useTranslation();
  useDocumentTitle("dashboard.title");
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const { formatCurrency } = useCurrencyFormat();
  const { data: invoiceData } = useInvoices({ page: 1, limit: 100 });
  const { data: customers } = useCustomers();
  const { level, availableFeatures, getLimitText } = useSubscription();
  const invoices = invoiceData?.data || [];
  const allCustomers = customers || [];

  // Year selection for revenue chart
  const now = new Date();
  const currentYear = now.getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Filter invoices by status
  const openInvoices = invoices.filter((inv: Invoice) => inv.status === "sent");
  const overdueInvoices = invoices.filter((inv: Invoice) => {
    if (inv.status !== "sent") return false;
    return new Date(inv.dueDate) < new Date();
  });
  const paidInvoices = invoices.filter((inv: Invoice) => inv.status === "paid");

  // Calculate amounts
  const openAmount = openInvoices.reduce(
    (sum: number, inv: Invoice) => sum + parseFloat(inv.total),
    0,
  );
  const overdueAmount = overdueInvoices.reduce(
    (sum: number, inv: Invoice) => sum + parseFloat(inv.total),
    0,
  );

  // Calculate revenue
  const currentMonth = now.getMonth();

  const monthlyRevenue = paidInvoices
    .filter((inv: Invoice) => {
      if (!inv.paidAt) return false;
      const paidDate = new Date(inv.paidAt);
      return (
        paidDate.getMonth() === currentMonth &&
        paidDate.getFullYear() === currentYear
      );
    })
    .reduce((sum: number, inv: Invoice) => sum + parseFloat(inv.total), 0);

  const yearlyRevenue = paidInvoices
    .filter((inv: Invoice) => {
      if (!inv.paidAt) return false;
      const paidDate = new Date(inv.paidAt);
      return paidDate.getFullYear() === selectedYear;
    })
    .reduce((sum: number, inv: Invoice) => sum + parseFloat(inv.total), 0);

  // Get all years with paid invoices
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    paidInvoices.forEach((inv: Invoice) => {
      if (inv.paidAt) {
        years.add(new Date(inv.paidAt).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [paidInvoices]);

  // Get next/previous available year
  const nextAvailableYear = useMemo(() => {
    const nextYears = availableYears.filter((year) => year > selectedYear);
    return nextYears.length > 0 ? nextYears[0] : null;
  }, [availableYears, selectedYear]);

  const previousAvailableYear = useMemo(() => {
    const prevYears = availableYears.filter((year) => year < selectedYear);
    return prevYears.length > 0 ? prevYears[prevYears.length - 1] : null;
  }, [availableYears, selectedYear]);

  // Calculate monthly revenue data for chart (all 12 months of selected year)
  const monthlyRevenueData = useMemo(() => {
    const months: { monthKey: string; monthName: string; revenue: number }[] =
      [];

    // Generate all 12 months of selected year
    for (let i = 0; i <= 11; i++) {
      const date = new Date(selectedYear, i, 1);
      const monthKey = `${selectedYear}-${String(i + 1).padStart(2, "0")}`;
      months.push({
        monthKey,
        monthName: date.toLocaleDateString("de-DE", { month: "short" }),
        revenue: 0,
      });
    }

    // Aggregate revenue by month
    paidInvoices.forEach((invoice: Invoice) => {
      if (!invoice.paidAt) return;
      const paidDate = new Date(invoice.paidAt);
      if (paidDate.getFullYear() !== selectedYear) return;
      const monthKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, "0")}`;
      const monthData = months.find((m) => m.monthKey === monthKey);
      if (monthData) {
        monthData.revenue += parseFloat(invoice.total);
      }
    });

    return months.map(({ monthName, revenue }) => ({
      month: monthName,
      revenue,
    }));
  }, [paidInvoices, selectedYear]);

  // Get recent data
  const recentInvoices = [...invoices]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 5);

  const recentCustomers = [...allCustomers]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 5);

  // Helper functions
  const getStatusVariant = (status: InvoiceStatus) => {
    if (status === "draft") return "secondary";
    if (status === "sent") return "destructive";
    return "default";
  };

  const getStatusClassName = (status: InvoiceStatus) => {
    return status === "paid"
      ? "bg-green-100 text-green-800 hover:bg-green-100 border-green-200"
      : "";
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            {t("dashboard.title")}
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            {t("dashboard.welcome")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/customers/new"
            className="flex-1 sm:flex-initial"
          >
            <Button variant="outline" className="w-full sm:w-auto">
              <Users className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">
                {t("dashboard.newCustomer")}
              </span>
              <span className="sm:hidden">{t("customers.new")}</span>
            </Button>
          </Link>
          <Link to="/dashboard/invoices/new" className="flex-1 sm:flex-initial">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">
                {t("dashboard.newInvoice")}
              </span>
              <span className="sm:hidden">{t("invoices.new")}</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium sm:text-sm">
              {t("dashboard.openInvoices")}
            </CardTitle>
            <Clock className="text-muted-foreground h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold sm:text-2xl">
              {openInvoices.length}
            </div>
            <p className="text-muted-foreground text-xs">
              {formatCurrency(openAmount)}
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium sm:text-sm">
              {t("dashboard.overdueInvoices")}
            </CardTitle>
            <AlertTriangle className="h-3.5 w-3.5 text-red-500 sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-red-600 sm:text-2xl">
              {overdueInvoices.length}
            </div>
            <p className="text-xs text-red-600">
              {formatCurrency(overdueAmount)}
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium sm:text-sm">
              {t("dashboard.revenueThisMonth")}
            </CardTitle>
            <Calendar className="text-muted-foreground h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold sm:text-2xl">
              {formatCurrency(monthlyRevenue)}
            </div>
            <p className="text-muted-foreground text-xs">
              {
                paidInvoices.filter(
                  (inv: Invoice) =>
                    new Date(inv.updatedAt).getMonth() === currentMonth &&
                    new Date(inv.updatedAt).getFullYear() === currentYear,
                ).length
              }{" "}
              {t("dashboard.invoicesPaid")}
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium sm:text-sm">
              {selectedYear === currentYear
                ? t("dashboard.revenueThisYear")
                : t("dashboard.revenueForYear", { year: selectedYear })}
            </CardTitle>
            <TrendingUp className="text-muted-foreground h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold sm:text-2xl">
              {formatCurrency(yearlyRevenue)}
            </div>
            <p className="text-muted-foreground text-xs">
              {
                paidInvoices.filter(
                  (inv: Invoice) =>
                    new Date(inv.updatedAt).getFullYear() === selectedYear,
                ).length
              }{" "}
              {t("dashboard.invoicesTotal")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Revenue Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base md:text-lg">
                {t("dashboard.monthlyRevenueChart.title")}
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                {t("dashboard.monthlyRevenueChart.descriptionForYear", {
                  year: selectedYear,
                })}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (previousAvailableYear)
                    setSelectedYear(previousAvailableYear);
                }}
                disabled={previousAvailableYear === null}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[3rem] text-center text-sm font-medium">
                {selectedYear}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (nextAvailableYear) setSelectedYear(nextAvailableYear);
                }}
                disabled={nextAvailableYear === null}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-2">
          <ResponsiveContainer
            width="100%"
            height={180}
            className="sm:h-[200px]"
          >
            <BarChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10 }}
                className="text-xs sm:text-sm"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                className="text-xs sm:text-sm"
              />
              <Tooltip
                formatter={(value: number) => [
                  formatCurrency(value),
                  t("dashboard.monthlyRevenueChart.revenue"),
                ]}
                contentStyle={{ fontSize: "12px" }}
              />
              <Bar
                dataKey="revenue"
                fill="hsl(var(--primary))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Activities */}
      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        {/* Recent Invoices */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base md:text-lg">
              <FileText className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              {t("dashboard.recentInvoices")}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              {t("dashboard.recentInvoicesDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentInvoices.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.number")}
                        </TableHead>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.amount")}
                        </TableHead>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.status")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentInvoices.map((invoice: Invoice) => (
                        <TableRow
                          key={invoice.id}
                          className="hover:bg-muted/50 cursor-pointer"
                          onClick={() =>
                            navigate({
                              to: `/dashboard/invoices/edit/$id`,
                              params: { id: invoice.id },
                            })
                          }
                        >
                          <TableCell className="text-xs font-medium md:text-sm">
                            {invoice.invoiceNumber}
                          </TableCell>
                          <TableCell className="text-xs md:text-sm">
                            {formatCurrency(parseFloat(invoice.total))}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={getStatusVariant(invoice.status)}
                              className={
                                getStatusClassName(invoice.status) + " text-xs"
                              }
                            >
                              {t(`invoiceStatus.${invoice.status}`)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-3">
                  <Link to="/dashboard/invoices">
                    <Button variant="outline" size="sm" className="w-full">
                      {t("dashboard.viewAllInvoices")}
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground py-4 text-center">
                {t("dashboard.noInvoicesYet")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Customers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base md:text-lg">
              <Users className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              {t("dashboard.recentCustomers")}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              {t("dashboard.recentCustomersDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentCustomers.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.name")}
                        </TableHead>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.type")}
                        </TableHead>
                        <TableHead className="text-xs md:text-sm">
                          {t("dashboard.date")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentCustomers.map((customer: CustomerWithEmails) => {
                        const name =
                          customer.customerType === "business"
                            ? customer.companyName
                            : `${customer.firstName} ${customer.lastName}`;

                        return (
                          <TableRow
                            key={customer.id}
                            className="hover:bg-muted/50 cursor-pointer"
                            onClick={() =>
                              navigate({
                                to: `/dashboard/customers/$id`,
                                params: { id: customer.id },
                              })
                            }
                          >
                            <TableCell className="text-xs font-medium md:text-sm">
                              {name}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  customer.customerType === "business"
                                    ? "default"
                                    : "secondary"
                                }
                                className="text-xs"
                              >
                                {customer.customerType === "business"
                                  ? t("dashboard.business")
                                  : t("dashboard.private")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs md:text-sm">
                              {formatDate(new Date(customer.createdAt))}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-3">
                  <Link to="/dashboard/customers">
                    <Button variant="outline" size="sm" className="w-full">
                      {t("dashboard.viewAllCustomers")}
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground py-4 text-center">
                {t("dashboard.noCustomersYet")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Overdue Warning */}
      {overdueInvoices.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center text-red-800">
              <AlertTriangle className="mr-2 h-5 w-5" />
              {t("dashboard.overduePayments")}
            </CardTitle>
            <CardDescription className="text-red-700">
              {t("dashboard.overduePaymentsDesc", {
                count: overdueInvoices.length,
                plural: overdueInvoices.length > 1 ? "en" : "",
                verb: overdueInvoices.length > 1 ? "sind" : "ist",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.invoiceNumber")}</TableHead>
                  <TableHead>{t("dashboard.dueDate")}</TableHead>
                  <TableHead>{t("dashboard.amount")}</TableHead>
                  <TableHead>{t("dashboard.daysOverdue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overdueInvoices.slice(0, 5).map((invoice: Invoice) => {
                  const daysOverdue = Math.floor(
                    (new Date().getTime() -
                      new Date(invoice.dueDate).getTime()) /
                      (1000 * 60 * 60 * 24),
                  );

                  return (
                    <TableRow
                      key={invoice.id}
                      className="cursor-pointer hover:bg-red-100"
                      onClick={() =>
                        navigate({
                          to: `/dashboard/invoices/edit/$id`,
                          params: { id: invoice.id },
                        })
                      }
                    >
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        {formatDate(new Date(invoice.dueDate))}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(parseFloat(invoice.total))}
                      </TableCell>
                      <TableCell className="font-semibold text-red-600">
                        {daysOverdue} {t("dashboard.days")}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {overdueInvoices.length > 5 && (
              <p className="mt-2 text-sm text-red-700">
                {t("dashboard.andMoreOverdue", {
                  count: overdueInvoices.length - 5,
                  plural: overdueInvoices.length - 5 > 1 ? "en" : "",
                })}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscription Info - Development Only */}
      {import.meta.env.DEV && (
        <Card>
          <CardHeader>
            <CardTitle>Subscription Status</CardTitle>
            <CardDescription>
              Your current subscription level and available features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p>
                <strong>Level:</strong> {level.toUpperCase()}
              </p>
              <p>
                <strong>Available Features:</strong> {availableFeatures.length}
              </p>
              <div className="space-y-1">
                <p>
                  <strong>Limits:</strong>
                </p>
                <div className="text-muted-foreground space-y-1 pl-2 text-sm">
                  <p>
                    Customers: {getLimitText("customers", allCustomers.length)}
                  </p>
                  <p>Invoices: {getLimitText("invoices", invoices.length)}</p>
                  <p>Templates: {getLimitText("templates", 0)}</p>
                  <p>Storage: {getLimitText("storage", 0)} MB</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {availableFeatures.map((feature) => (
                  <Badge key={feature.id} variant="secondary">
                    {feature.name}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
